import { CSSProperties, useEffect, useState } from "react";
import { BrandContent } from "modules/Types";
import { pageView } from "analytics";
import Sty from "modules/Sty";
import WebsiteListItem from "./WebsiteListItem";
import {
    formattedWebsiteUrl,
    generateShortUniqueId,
    getSearchParam,
    getStatusToShowPaymentFailedBanner,
    makeKey,
    slugToTitle,
    styx
} from 'modules/Utils';

import { WEBSITE_CREATION_TYPE } from "modules/Types";
import { OverallWebsiteDashboard } from "./OverallWebsiteDashboard/OverallWebsiteDashboard";
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import DEPRECATED_useOrganization from 'Hooks/useOrganization';
import OrganizationSubscriptionStatusBanner from "components/shareable/OrganizationSubscriptionStatusBanner";
import Colors from "modules/Colors";
import PageSymphony from "elements/PageSymphony";
import DefaultAdditionalActions from "components/shareable/DefaultAdditionalActions";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import TabSymphony from "elements/TabSymphony";
import CardSymphony from "elements/CardSymphony";
import useWebsiteBuilder, { getWebsiteBuilderPath } from "Hooks/useWebsiteBuilder";
import WebsiteBuilderMiniature from "components/shareable/WebsiteBuilderMiniature";
import Icon from "elements/Icon";
import BrandInfo from "elements/BrandInfo";
import FeatureGate from 'components/FeatureGate';
import { FeatureKeys } from 'features/definitions';
import dayjs from "dayjs";
import TagSymphony from "elements/TagSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import { useHistory } from "react-router-dom";
import { SYMPHONY_WEBSITE_URL, WebsiteTemplateCategory } from "modules/Const";
import { WebsiteType } from "modules/Const";
import DebugBox from "components/shareable/DebugBox";
import useScreen from "Hooks/useScreen";
import { toast } from "react-toastify";
import WebsiteGallery from "components/WebsiteGallery";
import useAppView from "Hooks/useAppView";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import useGlobalModals from "Hooks/useGlobalModals";
import useWebsites from "Hooks/useWebsites";
import { useViewOnlyAccess, ViewOnlyFeature } from "Hooks/useViewOnlyAccess";

import Loader from "elements/Loader";
import Banner from "elements/Banner";
import ConnectSection from "./CustomizeWebsite/components/ConnectSection/ConnectSection";
import CreateNewWebsiteModalV2 from "components/shareable/CreateNewWebsiteModalV2";
import { useFeature } from "Hooks/useFeature";

const isDev = process.env.NODE_ENV === 'development';

enum WebsitePageTabs {
    SITES = 'sites',
    DASHBOARD = 'dashboard',
    PIXELS = 'pixels',
    GALLERY = 'gallery',
}

const WEBSITE_TABS = [
    {
        id: WebsitePageTabs.SITES,
        title: "Websites"
    },
    {
        id: WebsitePageTabs.DASHBOARD,
        title: "Dashboard"
    },
    {
        id: WebsitePageTabs.PIXELS,
        title: "Pixels"
    },
    {
        id: WebsitePageTabs.GALLERY,
        title: "Gallery"
    }
]

const WEBSITE_TABS_TEAM = [
    {
        id: WebsitePageTabs.SITES,
        title: "Websites"
    }
]

const WebsitePage = () => {
    const history = useHistory();
    const {
        mStyle,
        mobileView
    } = useScreen();
    const { isTeamView } = useAppView();
    const { organization } = useOrganizationV2();
    const { openBrandSelector } = useGlobalModals();

    const {
        currentBrand,
        isBrandAdmin,
        loadedBrandsDict
    } = useBrand();

    // Check View Only access for website features
    const { hasAccess: canAccessGallery } = useViewOnlyAccess(ViewOnlyFeature.WEBSITE_GALLERY);
    const { hasAccess: canEditSite } = useViewOnlyAccess(ViewOnlyFeature.WEBSITE_EDIT_SITE);
    const { isAvailable: hasAccessToEditSite } = useFeature(FeatureKeys.WEBSITE_EDITING);

    const {
        legacyWebsites,
        websiteBuilderSites,
        organizationWebsites,
        isLoadingLegacyWebsites,
        isLoadingWebsiteBuilder,
        getLegacyWebsites,
        getWebsiteBuilderWebsites,
        getOrganizationWebsites,
        isLegacyWebsitesInitialized,
        isWebsiteBuilderInitialized
    } = useWebsites();

    const {
        setInitialWebsiteType,
        setShowCreateNewWebsiteModal,
        isLoadingWebsitesGallery,
        setSelectedTemplateCategory,
    } = useWebsiteBuilder();

    const { openCreateWebsite } = useGlobalModals();

    // State for brand filtering in team view
    const [selectedBrands, setSelectedBrands] = useState<number[]>([]);

    const isBothWebsitesInitialized = isWebsiteBuilderInitialized && isLegacyWebsitesInitialized
    const isEitherWebsiteLoading = isLoadingLegacyWebsites || isLoadingWebsiteBuilder
    const isBothWebsitesEmpty = legacyWebsites?.length === 0 && websiteBuilderSites?.length === 0

    const websiteSlug = generateShortUniqueId()
    const websiteBuilderPath = currentBrand ? getWebsiteBuilderPath(currentBrand.slug, websiteSlug) : '#'

    const templateCategoryParam = getSearchParam({ name: 'templateCategory' });




    const showPaymentFailedBanner = currentBrand ? getStatusToShowPaymentFailedBanner(currentBrand) : false

    // Filter tabs based on view mode and permissions
    const getVisibleWebsiteTabs = () => {
        if (isTeamView()) {
            return WEBSITE_TABS_TEAM;
        }

        // Filter out Gallery tab for View Only users
        return WEBSITE_TABS.filter(tab => {
            if (tab.id === WebsitePageTabs.GALLERY && !canAccessGallery) {
                return false;
            }
            return true;
        });
    };

    const availableTabs = getVisibleWebsiteTabs();
    const [selectedWebsiteTab, setSelectedWebsiteTab] = useState<TabItem>(availableTabs[0]);

    const [actionedItem, setActionedItem] = useState<BrandContent | undefined>(undefined)

    const openCreateNewWebsiteModal = (websiteKey?: string, redirectImmediately?: boolean) => {
        // In Team view, first show brand selector
        if (isTeamView() && !currentBrand) {
            openBrandSelector({
                nextAction: 'create_website',
                source: 'website_page_team_view',
                title: 'Select a Profile for Website',
                subtitle: 'Choose which profile you want to create a website for.',
                websiteTemplateCategory: websiteKey as WebsiteTemplateCategory,
                websiteType: websiteKey as WebsiteType
            });
            return;
        }

        openCreateWebsite({
            templateCategory: websiteKey as WebsiteTemplateCategory,
            websiteType: websiteKey as WebsiteType,
            redirectToWebsiteBuilderImmediately: redirectImmediately, // Override smart default if specified
            source: redirectImmediately ? 'website_page_brand_view_create' : 'website_page_create_modal'
        });
    }

    const handleCreatePresave = () => {
        // In Team view, first show brand selector  
        if (isTeamView() && !currentBrand) {
            openBrandSelector({
                nextAction: 'create_campaign', // Required but not used when customAction is provided
                source: 'website_page_team_view_presave',
                title: 'Select a Profile for Presave',
                subtitle: 'Choose which profile you want to create a presave for.',
                customAction: (brand) => {
                    // After brand selection, navigate directly to presave page
                    history.push('/marketing/new/presave');
                }
            });
            return;
        }

        history.push(`/marketing/new/presave`);
    }

    useEffect(() => {
        if (templateCategoryParam) {
            openCreateNewWebsiteModal(templateCategoryParam);
        }

        // Fetch websites based on view type
        if (isTeamView() && organization?.id) {
            // Fetch organization websites
            getOrganizationWebsites({ organizationId: organization.id });
        } else if (currentBrand?.id) {
            // Fetch brand websites
            const brandId = currentBrand.id;
            getWebsiteBuilderWebsites({ brandId });
            getLegacyWebsites({ brandId });
        }
    }, [currentBrand?.id, organization?.id, isTeamView()]);

    useEffect(() => {
        pageView("Website List")
    }, [currentBrand])

    useEffect(() => {
        if (selectedWebsiteTab.id === WebsitePageTabs.DASHBOARD) {
            pageView("Website Dashboard")
        }
    }, [selectedWebsiteTab.id])

    // Render action buttons for website builder sites
    const renderWebsiteBuilderActionButtons = (brandSlug: string, slug: string, websiteLink: string) => {
        const buttonSize = mobileView ? "small" : undefined;

        const items = [];

        if (canEditSite && hasAccessToEditSite) {
            items.push(
                <>
                    <ButtonSymphony
                        className="websiteBuilderSiteEditButton"
                        variant="text"
                        onClick={() => {
                            history.push(`/website-builder/${brandSlug}/${slug}`)
                        }}
                        size={buttonSize}
                    >
                        Edit Site
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="websiteBuilderSiteEditButton"
                        variant="text"
                        onClick={() => {
                            history.push(`/website-builder/${brandSlug}/${slug}?tab=analytics`)
                        }}
                        size={buttonSize}
                    >
                        View Analytics
                    </ButtonSymphony>
                    <ButtonSymphony
                        key="copy-link"
                        className="websiteBuilderSiteEditButton"
                        variant="text"
                        onClick={() => {
                            navigator.clipboard.writeText(websiteLink);
                        }}
                        size={buttonSize}
                    >
                        Copy Link
                    </ButtonSymphony>
                </>
            );

        } else {
            return (
                <FeatureGate
                    key="edit-site"
                    feature={FeatureKeys.WEBSITE_EDITING}
                    upgradeSource="Website Page Edit Button"
                    upgradeCallToAction="Upgrade to edit your websites"
                    buttonText="Upgrade to Edit"
                >
                </FeatureGate>
            )
        }
    };

    // Consolidated function to render a website builder row
    const renderWebsiteBuilderRow = (website: any, index: number, brandInfo?: any) => {
        const {
            slug,
            updatedAt,
            createdAt,
            websiteContent
        } = website;

        const effectiveBrandInfo = brandInfo || website.brandInfo;
        const brandSlug = effectiveBrandInfo?.slug || currentBrand?.slug;
        const brandName = effectiveBrandInfo?.name || currentBrand?.name;
        const brandImage = effectiveBrandInfo?.image || currentBrand?.image;

        const websiteLink = `${SYMPHONY_WEBSITE_URL}/${brandSlug}/${slug}`;
        const websiteLinkProd = `https://symphony.to/${brandSlug}/${slug}`;

        return (
            <div
                key={makeKey('websiteBuilderSite', index)}
                className="websiteBuilderSiteRow"
                style={mStyle(sty, 'websiteBuilderSiteRow')}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <WebsiteBuilderMiniature
                        className="websiteHomepageMiniature"
                        style={{
                            cursor: 'pointer',
                        }}
                        website={website}
                        onClick={() => {
                            history.push(`/website-builder/${brandSlug}/${slug}`)
                        }}
                    />
                </div>

                <div className="websiteBuilderSiteInfoContainer" style={mStyle(sty, 'websiteBuilderSiteInfoContainer')}>
                    <div className="websiteBuilderSiteInfo" style={sty.websiteBuilderSiteInfo}>
                        <div>
                            <div className="websiteBuilderSiteTitle">
                                {slugToTitle(slug)}
                            </div>
                            {isTeamView() && effectiveBrandInfo && (
                                <BrandInfo
                                    brandName={brandName}
                                    brandImage={brandImage}
                                    brandSlug={brandSlug}
                                    variant="small"
                                    titleColor={Colors.greyDark}
                                />
                            )}
                        </div>
                        <div className="websiteBuilderSiteTags" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <TagSymphony
                                className="websiteBuilderSiteStatus"
                                title="Website Builder"
                                type="square"
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <div style={Sty.text12grey}>
                                Created {dayjs(createdAt).fromNow()} –
                            </div>
                            <div style={styx(Sty.text12grey, { fontStyle: 'italic' })}>
                                (Updated {dayjs(updatedAt).fromNow()})
                            </div>
                        </div>
                        <div
                            className="websiteBuilderSiteLinkContainerOuter"
                            style={sty.websiteBuilderSiteLinkContainerOuter}
                        >
                            <div
                                className="websiteBuilderSiteLinkContainer"
                                style={sty.websiteBuilderSiteLinkContainer}
                                onClick={() => {
                                    navigator.clipboard.writeText(websiteLink);
                                    toast.success('Link copied to clipboard');
                                }}
                            >
                                <div
                                    className="websiteBuilderSiteLink"
                                    style={{ color: Colors.purple, fontSize: 14 }}
                                >
                                    {websiteLink}
                                </div>
                                <Icon
                                    className="websiteBuilderSiteLinkIcon"
                                    icon="link"
                                    size={16}
                                    color={Colors.greyDark}
                                />
                            </div>
                            <Icon
                                className="websiteBuilderSiteLinkIconExternal"
                                icon="externalLink"
                                size={22}
                                onClick={() => {
                                    window.open(websiteLink, '_blank');
                                }}
                            />
                            {isDev && (
                                <Icon
                                    className="websiteBuilderSiteLinkIconExternal"
                                    icon="externalLink"
                                    size={22}
                                    color={Colors.green}
                                    onClick={() => {
                                        window.open(websiteLinkProd, '_blank');
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    <div className="websiteBuilderSiteActions" style={mStyle(sty, 'websiteBuilderSiteActions')}>
                        {renderWebsiteBuilderActionButtons(brandSlug, slug, websiteLink)}
                    </div>
                </div>
            </div>
        );
    };

    const renderWebsiteBuilderSites = () => {
        let sitesToRender = isTeamView()
            ? [...organizationWebsites.websiteBuilderSites]
            : [...websiteBuilderSites];

        // Filter by selected brands in team view
        if (isTeamView() && selectedBrands.length > 0) {
            sitesToRender = sitesToRender.filter(site =>
                site.brandInfo && selectedBrands.includes(site.brandInfo.id)
            );
        }

        // Sort by most recent
        sitesToRender = sitesToRender.sort((a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );

        return sitesToRender.map((website, index) =>
            renderWebsiteBuilderRow(website, index)
        );
    }

    const renderViewOnlyBanner = () => {
        // Only show for non-admin users in brand view (not team view)
        if (isTeamView() || isBrandAdmin || !currentBrand) {
            return null;
        }

        return (
            <Banner
                className="websiteListTabSitesNoWebsitesBanner"
                style={{ marginTop: 16 }}
                title={`You have "View Only" access to ${currentBrand.name}`}
                description="Ask your team admin to add you as a full admin to this brand in order to create websites."
                color="yellow"
            />
        );
    };

    const renderZeroState = () => {
        // Check if we should show the zero state
        if (isTeamView()) {
            // Team view: Check if organization has no websites
            const hasNoWebsites = organizationWebsites.legacyWebsites.length === 0 &&
                organizationWebsites.websiteBuilderSites.length === 0;

            if (!hasNoWebsites) {
                return null;
            }

            // Team view zero state - show brand selector instead of template selector
            return (
                <CardSymphony
                    className="websiteListHeader"
                    title="Create your first website"
                    isNoPadding
                    size="small"
                >
                    <div style={{
                        padding: 24,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        alignItems: 'center'
                    }}>
                        <Icon
                            className="teamZeroStateIcon"
                            icon="website"
                            size={48}
                            color={Colors.greyDark}
                        />
                        <div>
                            <div style={{
                                fontSize: 16,
                                fontWeight: 600,
                                marginBottom: 8,
                                color: Colors.textColor
                            }}>
                                Get started with your first website
                            </div>
                            <div style={{
                                fontSize: 14,
                                color: Colors.greyDark,
                                marginBottom: 16
                            }}>
                                Choose a profile to create a website for, then select from our templates.
                            </div>
                        </div>
                        <ButtonSymphony
                            className="teamZeroStateCreateWebsiteButton"
                            onClick={() => openCreateNewWebsiteModal()}
                        >
                            Create Website
                        </ButtonSymphony>
                    </div>
                </CardSymphony>
            );
        } else {
            // Brand view: Check various conditions
            if (!isBrandAdmin) {
                // Non-admin users see the "View Only" banner instead
                return null;
            }

            if (!isWebsiteBuilderInitialized || !isLegacyWebsitesInitialized) {
                // Still loading
                return null;
            }

            const hasNoWebsites = legacyWebsites?.length === 0 && websiteBuilderSites?.length === 0;
            if (!hasNoWebsites) {
                return null;
            }

            // Brand view zero state - show template selector directly
            return (
                <CardSymphony
                    className="websiteListHeader"
                    title="Create your first site"
                    isNoPadding
                    size="small"
                >
                    <CreateNewWebsiteModalV2
                        className="createNewWebsiteModalV2FromWebsitePage"
                        isModal={false}
                        isRedirect
                    />
                </CardSymphony>
            );
        }
    };

    const renderLegacyWebsiteItems = () => {
        // In Team view, render organization legacy websites
        if (isTeamView()) {
            let sitesToRender = organizationWebsites.legacyWebsites;

            // Filter by selected brands if any
            if (selectedBrands.length > 0) {
                sitesToRender = sitesToRender.filter(site =>
                    site.brandInfo && selectedBrands.includes(site.brandInfo.id)
                );
            }

            if (sitesToRender.length === 0) {
                return null;
            }

            return sitesToRender.map((item: any, index: number) => {
                const brandInfo = item.brandInfo;
                const thumbnail = item.content_metadata?.thumbnail_url || brandInfo?.image || ''
                const primaryTitle = item.name
                let websiteType: WebsiteType = WebsiteType.RECORD_STREAMS
                let additionalLabel = null

                let hasPresaveDetails = 'presave' in (item.content_metadata || {})
                if (hasPresaveDetails && !(item.content_metadata.presave?.saved_at)) {
                    websiteType = WebsiteType.PRESAVE
                } else if (item.type === WebsiteType.DATA_COLLECTOR) {
                    websiteType = WebsiteType.DATA_COLLECTOR
                } else if (item.type === WebsiteType.TOUR) {
                    websiteType = WebsiteType.TOUR
                } else {
                    switch (item.content_metadata?.type) {
                        case 'single':
                        case 'record':
                            additionalLabel = 'Single'
                            break;
                        case 'album':
                        case 'project':
                            additionalLabel = 'Album/Project'
                            break;
                        case 'playlist':
                            additionalLabel = 'Playlist'
                            break;
                    }
                }

                const websiteUrl = `${SYMPHONY_WEBSITE_URL}/${brandInfo.slug}/${item.slug}`;
                let linkToEdit = `/website/${item.type}/${item.slug}`;

                // Return WebsiteListItem with brand info
                return (
                    <WebsiteListItem
                        key={makeKey('websiteListItemOrg', item.id)}
                        content={item}
                        setActionedItem={setActionedItem}
                        titleText={primaryTitle}
                        thumbnailSrc={thumbnail}
                        subtitle={null}
                        websiteUrl={websiteUrl}
                        linkToEdit={linkToEdit}
                        onClick={() => { }}
                        creationType={item.content_metadata?.publishReason === 'marketing_campaign' ?
                            WEBSITE_CREATION_TYPE.CAMPAIGN : WEBSITE_CREATION_TYPE.AUTOMATIC}
                        websiteType={websiteType}
                        websiteSubtypeLabel={additionalLabel}
                        createdAt={item.createdAt}
                        updatedAt={item.updatedAt}
                        // TODO: Add brand info display to WebsiteListItem component
                        websiteStyle={{
                            bgType: item.primary_customization?.bgType,
                            primaryColor: item.primary_customization?.primaryColor,
                            buttonConfig: item.primary_customization?.buttonConfig,
                            secondaryColor: item.primary_customization?.secondaryColor,
                            bgColor: item.primary_customization?.bgColor,
                        }}
                    />
                )
            });
        }

        return legacyWebsites?.map((item: BrandContent, index: number) => {
            const thumbnail = item.content_metadata.thumbnail_url || currentBrand?.image || ''
            const primaryTitle = item.name
            let websiteType: WebsiteType = WebsiteType.RECORD_STREAMS
            let additionalLabel = null

            let hasPresaveDetails = 'presave' in item.content_metadata
            // if this is a presave, but it hasnt been saved yet - mark it as a presave site
            if (hasPresaveDetails && !(item.content_metadata.presave?.saved_at)) {
                websiteType = WebsiteType.PRESAVE
            } else if (item.type === WebsiteType.DATA_COLLECTOR) {
                websiteType = WebsiteType.DATA_COLLECTOR
            } else if (item.type === WebsiteType.TOUR) {
                websiteType = WebsiteType.TOUR
            } else {
                switch (item.content_metadata.type) {
                    case 'single':
                    case 'record':
                        additionalLabel = 'Single'
                        break;
                    case 'album':
                    case 'project':
                        additionalLabel = 'Album/Project'
                        break;
                    case 'playlist':
                        additionalLabel = 'Playlist'
                        break;
                }
            }

            let mainStyle = {
                bgType: 'artwork',
                bgColor: Colors.textColor as string,
                primaryColor: Colors.purple as string,
                secondaryColor: Colors.textColor as string,
                buttonConfig: "boxes"
            }

            if (item.primary_customization) {
                mainStyle = {
                    ...mainStyle,
                    ...item.primary_customization
                }
            } else if (item.content_metadata.website?.style) {
                mainStyle = {
                    ...mainStyle,
                    ...item.content_metadata.website.style
                }
            } else if (hasPresaveDetails && item.content_metadata.presave?.style) {
                mainStyle = {
                    ...mainStyle,
                    ...('presave' in item.content_metadata && item.content_metadata.presave?.style)
                }
            }

            let creationType: WEBSITE_CREATION_TYPE = WEBSITE_CREATION_TYPE.AUTOMATIC
            if (item.content_metadata.publishReason === 'marketing_campaign') {
                creationType = WEBSITE_CREATION_TYPE.CAMPAIGN
            }
            else if (hasPresaveDetails) {
                creationType = WEBSITE_CREATION_TYPE.PRESAVE
            }

            const websiteUrl = formattedWebsiteUrl(currentBrand, item)

            let linkToEdit = `/website/${item.type}/${item.slug}`
            if (item.content_metadata.presave && !item.content_metadata.presave.saved_at) {
                const linkedPresaveCampaigns = item.linked_campaigns?.filter(campaign => campaign.campaign_metadata.campaign_type === 'pre_save')
                if (linkedPresaveCampaigns && linkedPresaveCampaigns?.length > 0) {
                    linkToEdit = `/marketing/campaigns/${linkedPresaveCampaigns[0].id}`
                }
            }

            return (
                <WebsiteListItem
                    key={makeKey('websiteListItem', item.id)}
                    content={item}
                    setActionedItem={setActionedItem}
                    titleText={primaryTitle}
                    thumbnailSrc={thumbnail}
                    subtitle={null}
                    websiteUrl={websiteUrl}
                    linkToEdit={linkToEdit}
                    onClick={() => { }}
                    creationType={creationType}
                    websiteType={websiteType}
                    websiteSubtypeLabel={additionalLabel}
                    createdAt={item.createdAt}
                    updatedAt={item.updatedAt}
                    websiteStyle={{
                        bgType: mainStyle?.bgType,
                        primaryColor: mainStyle?.primaryColor,
                        buttonConfig: mainStyle?.buttonConfig,
                        secondaryColor: mainStyle?.secondaryColor,
                        bgColor: mainStyle?.bgColor,
                    }}
                />
            )
        })
    }

    // NOTE: render()
    return (
        <>
            <PageSymphony
                className="websiteListComponent"
                isContent
            >

                <CardSymphony
                    className="cardTitleWebsiteList"
                    title="Website"
                    subtitle="Own your audience and grow with our customizable website builder."
                    additionalButtons={
                        <>
                            {(isTeamView() || isBrandAdmin) &&
                                <DefaultAdditionalActions
                                    className="defaultAdditionalActionsWebsiteList"
                                    additionalItemsText="Popular Websites:"
                                    buttonText="Create Website"
                                    buttonText2="Create Presave"
                                    // onButtonClick={() => openCreateNewWebsiteModal()}
                                    onButtonClick={() => openCreateNewWebsiteModal(undefined, true)}
                                    onButtonClick2={handleCreatePresave}
                                    campaignItems={[
                                        {
                                            key: WebsiteTemplateCategory.MUSIC,
                                            title: "Promote Music",
                                            icon: 'musicSite',
                                            link: "#",
                                        },
                                        {
                                            key: WebsiteTemplateCategory.COLLECT_DATA,
                                            title: "Collect Fan Data",
                                            icon: 'dataCollectorSite',
                                            link: "#",
                                        },
                                        {
                                            key: WebsiteTemplateCategory.EVENTS,
                                            title: "Promote Events",
                                            icon: 'tourSite',
                                            link: "#",
                                        },
                                    ]}
                                    onCampaignItemClick={(websiteKey) => openCreateNewWebsiteModal(websiteKey, true)}
                                />
                            }
                        </>
                    }
                />
                <CardSymphony
                    className="websiteListTabsContainer"
                >
                    <TabsSymphony
                        className="websiteListTabs"
                        selectedTab={selectedWebsiteTab}
                        onChange={(tab) => setSelectedWebsiteTab(tab as TabItem)}
                        tabs={availableTabs}
                        isLoading={isLoadingLegacyWebsites || isLoadingWebsiteBuilder || isLoadingWebsitesGallery}
                        isBorderBottom
                    />

                    <TabSymphony
                        className="websiteListTabSites"
                        visible={selectedWebsiteTab.id === WebsitePageTabs.SITES}
                    >
                        {(!isBothWebsitesInitialized && (isBothWebsitesEmpty || isEitherWebsiteLoading)) &&
                            <Loader
                                className="websiteListTabSitesLoadingLoader"
                                title="Loading websites..."
                                isBackground
                            />
                        }
                        {renderWebsiteBuilderSites()}
                        {renderLegacyWebsiteItems()}
                        {renderViewOnlyBanner()}
                        {renderZeroState()}
                    </TabSymphony>

                    {selectedWebsiteTab.id === WebsitePageTabs.DASHBOARD &&
                        <TabSymphony
                            className="websiteListTabDashboard"
                            visible={selectedWebsiteTab.id === WebsitePageTabs.DASHBOARD}
                            style={{ gap: 24, paddingTop: 24 }}
                        >
                            <OverallWebsiteDashboard />
                        </TabSymphony>
                    }

                    {selectedWebsiteTab.id === WebsitePageTabs.PIXELS &&
                        <TabSymphony
                            className="websiteListTabPixels"
                            visible={selectedWebsiteTab.id === WebsitePageTabs.PIXELS}
                        >
                            <ConnectSection
                                className="websitePixelsConnectSection"
                                bannerTitle="Website pixel tracking for all your sites"
                                bannerSubtitle="Add multiple pixels for different partners or purposes. We'll include your ad campaign pixel automatically if it's different."
                                upgradeSource="Website - Pixels Section"
                                upgradeCallToAction="Unlock website pixel tracking and advanced analytics with Symphony Pro"
                            />
                        </TabSymphony>
                    }

                    {selectedWebsiteTab.id === WebsitePageTabs.GALLERY &&
                        <TabSymphony
                            className="websiteListTabGallery"
                            visible={selectedWebsiteTab.id === WebsitePageTabs.GALLERY}
                        >
                            <WebsiteGallery
                                className="websiteGalleryFromWebsitePage"
                                isButtonAbove
                                isRedirect
                            />
                        </TabSymphony>
                    }
                </CardSymphony>
            </PageSymphony>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    websiteBuilderSiteRow: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        alignItems: 'center',
        gap: 16,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
        borderBottom: Sty.border,
    },
    websiteBuilderSiteRow_mobile: {
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 0,
        paddingRight: 0,
    },
    websiteBuilderSiteInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginRight: 'auto',
    },
    websiteBuilderSiteLinkContainerOuter: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
    },
    websiteBuilderSiteLinkContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        borderRadius: 4,
        backgroundColor: Colors.greyLighter,
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 4,
        paddingBottom: 4,
        wordBreak: 'break-all',
        width: 'fit-content',
        cursor: 'pointer',
    },
    websiteBuilderSiteActions: {
        display: 'flex',
        flexDirection: 'row',
        maxWidth: 360,
        justifyContent: 'flex-end',
        width: '100%',
        gap: 20
    },
    websiteBuilderSiteActions_mobile: {
        justifyContent: 'space-between',
        gap: 'unset'
    },
    websiteBuilderSiteInfoContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        width: '100%',
    },
    websiteBuilderSiteInfoContainer_mobile: {
        flexDirection: 'column',
    }
}

export default WebsitePage;